/* eslint-disable react-hooks/exhaustive-deps */

import { countriesEN, Country } from "@/assets/locales/en/countries-en";
import { countriesFR } from "@/assets/locales/fr/countries-fr";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { SelectComponent } from "@/components/Select/Select.component";
import { SelectSearchComponent } from "@/components/SelectSearch/SelectSearch.component";
import { Button } from "@/components/ui/Button/Button";
import { InputForm } from "@/components/ui/Input/InputForm";
import { Label } from "@/components/ui/Input/Label";
import { AuthStore } from "@/store/auth/auth.store"
import { User } from "@/store/auth/user.model";
import { profileSchema } from "@/store/settings/settings.model";
import { Pages } from "@/utils/routes.utils";
import { useResponsive } from "@/utils/useResponsive";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PhoneInput, { CountryData } from "react-phone-input-2";
import { useNavigate } from "react-router-dom";
import { parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from "sonner";
import { useDeleteAccount, useUpdateProfile } from "@/store/settings/settings.service";
import { Modal } from "@/components/Modals/Modal.component";
import InfoIcon from "@/assets/svgs/info.svg?react";
import { SettingsStore } from "@/store/settings/settings.store";
import { Lang } from "@/store/wallet/product.model";
import { useLoadProfile } from "@/store/auth/auth.service";

const countries = {
  EN: countriesEN,
  FR: countriesFR,
}

export const SettingsScreen = () => {
  const { getUser, signOut, updateUser } = AuthStore();
  const { setLanguage, getLanguage } = SettingsStore();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const lang = getLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [keepDataModalOpen, setKeepDataModalOpen] = useState<boolean>(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState<boolean>(false);
  const [eraseData, setEraseData] = useState<boolean>(false);

  const { data: profileData } = useLoadProfile();

  const countryCode = (window.navigator.language || window.navigator.languages[0]).split('-')[0];

  useEffect(() => {
    (async () => {
      const user = await getUser();
      setUser(user);
    })();
  }, [setUser, getUser]);

  useEffect(() => {
    if (profileData) {
      (async () => {
        await updateUser(profileData);
        setUser(profileData);
      })();
    }
  }, [profileData]);

  const handleLogout = () => {
    signOut();
    navigate(Pages.SINGIN);
  }

  const genders = [
    {
      label: t('settings.gender.male'),
      value: 'MALE',
    },
    {
      label: t('settings.gender.female'),
      value: 'FEMALE',
    },
    {
      label: t('settings.gender.preferNotToSay'),
      value: 'I PREFER NOT TO SAY'
    },
    {
      label: t('settings.gender.other'),
      value: 'OTHER',
    }
  ];

  const languages = [
    {
      label: t('settings.language.english'),
      value: 'EN',
    },
    {
      label: t('settings.language.french'),
      value: 'FR',
    }
  ];

  const {
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(profileSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      phoneCode: '',
      birthdate: '',
      countryCode: '',
      country: '',
      nationality: '',
      gender: '',
      nationalityCode: ''
    },
  })

  useEffect(() => {
    if (user) {
      setValue('firstName', user.firstName ?? '');
      setValue('lastName', user.lastName ?? '');
      setValue('phoneNumber', user.phoneNumber ?? '');
      setValue('phoneCode', user.phoneCode ?? '');
      setValue('birthdate', user.birthdate ?? '')
      setValue('nationalityCode', user.nationalityCode ?? '')
      setValue('nationality', user.nationality ?? '')
      setValue('country', user.country ?? '')
      setValue('countryCode', user.countryCode ?? '')
      setValue('gender', user.gender ?? '')
    }
  }, [user])

  const formValues = watch();

  useEffect(() => {
    const fields: (keyof typeof formValues)[] = [
      'firstName',
      'lastName',
      'phoneNumber',
      'phoneCode',
      'birthdate',
      'countryCode',
      'country',
      'nationalityCode',
      'nationality',
      'gender'
    ];

    fields.forEach(field => {
      if (formValues[field]) {
        trigger(field);
      }
    });
  },
    [
      formValues.firstName,
      formValues.lastName,
      formValues.gender,
      formValues.phoneNumber,
      formValues.phoneCode,
      formValues.country,
      formValues.nationality,
      formValues.nationalityCode,
      formValues.nationalityCode,
      trigger,
    ]
  );

  const formatPhoneNumber = (phoneCode: string, phoneNumber: string): string => {
    if (!phoneNumber.startsWith(phoneCode.replace('+', ''))) return phoneNumber;

    const national = phoneNumber.slice(phoneCode.length - 1);
    return `${phoneCode} ${national}`;
  }

  const { mutate: updateProfile } = useUpdateProfile({
    onSuccess: () => {
      toast.success(t("settings.updateSuccess"));
      setLoading(false);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: deleteAccount } = useDeleteAccount({
    onSuccess: () => {
      handleLogout();
    },
    onError: (message) => {
      toast.error(message);
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trigger();
    if (isValid) {
      const phoneCode = `+${formValues.phoneCode}`;
      const phoneNumber = formatPhoneNumber(phoneCode, formValues.phoneNumber);

      setValue('phoneNumber', phoneNumber);
      setValue('phoneCode', phoneCode);

      const { countryCode, nationalityCode } = formValues;

      if (countryCode) {
        const { label: country } = countriesEN.find(el => el.value === countryCode) as Country;
        setValue('country', country);
      }

      if (nationalityCode) {
        const { label: nationality } = countriesEN.find(el => el.value === nationalityCode) as Country;
        setValue('nationality', nationality);
      }

      setLoading(true);
      updateProfile(formValues);
    }
  }

  const handleDeleteAccount = () => {
    deleteAccount(eraseData);
  }

  const Header = () => (
    <div className={`flex flex-col items-start ${isMobile ? 'gap-y-4' : 'gap-y-8'}`}>
      <p className={'text-headingXSmall'}>{t("header.SETTINGS")}</p>
      <p className={`${isMobile ? 'text-bodyMedium' : 'text-bodyXLarge'}`}>{t("settings.personalInfo")}</p>
    </div>
  );

  return (
    <div className={`flex flex-col flex-1 ${isMobile ? 'pt-8 px-4' : 'px-16 py-6 xl:px-16 md:px-8'}`}>
      <Header />
      <form className="pb-[20px] mb-[20px] border-b border-gray-200" onSubmit={handleSubmit}>
        <div
          className={`flex flex-col justify-start items-center w-full  ${isMobile ? 'gap-y-3' : 'grid grid-cols-2 grid-rows-4 items-start justify-start gap-3 '}`}>
          <InputForm
            value={formValues.firstName}
            error={errors.firstName?.message}
            handleChange={(value) => setValue('firstName', value, { shouldDirty: true, shouldTouch: true })}
            required
            label={t("inputs.firstName.label")}
            placeholder={t("inputs.firstName.placeholder")}
          />
          <InputForm
            value={formValues.lastName}
            error={errors.lastName?.message}
            handleChange={(value) => setValue('lastName', value)}
            required
            label={t("inputs.lastName.label")}
            placeholder={t("inputs.lastName.placeholder")}
          />
          <InputForm
            disabled
            value={user?.customer?.email}
            required
            label={t("inputs.email.label")}
            placeholder={t("inputs.email.placeholder")}
            type="email"
          />
          <div className="flex flex-col w-full h-full">
            <Label>{t("inputs.phoneNumber.label")}</Label>
            <PhoneInput
              country={'fr'}
              value={formValues.phoneNumber}
              onChange={(value, code) => {
                setValue('phoneNumber', value);
                setValue('phoneCode', (code as CountryData).dialCode);
              }}
              inputClass="!w-full !h-[52px] !bg-[#F7F7F7] !rounded-sm !px-[20px] !pl-[58px] !text-base !text-black !placeholder-[#7C7C7C] !placeholder:text-textSmall !border !border-[rgba(0,0,0,0.05)] focus:!ring-1 focus:!ring-ring focus:!outline-none"
              buttonClass="!bg-transparent !border-none !pl-[16px]"
              countryCodeEditable={false}
              preferredCountries={['fr']}
              localization={countryCode === 'fr' ? fr : {}}
              enableSearch={true}
              searchPlaceholder={t("inputs.phoneNumber.search")}
              disableSearchIcon={true}
            />
          </div>
          <SelectSearchComponent
            options={countries[lang]}
            clearIndicator={false}
            label={t('inputs.nationality.label')}
            handleChange={(v) => { setValue('nationalityCode', v) }}
            value={formValues.nationalityCode}
            placeholder={t('inputs.nationality.placeholder')}
            className={`w-full ${isMobile ? 'mt-3' : ''}`}
            labelClassName="text-bodyLarge leading-none pb-3 group-disabled:cursor-not-allowed group-disabled:opacity-70"
          />
          <div className={`w-full ${isMobile ? 'mt-10' : ''}`}>
            <DatePicker
              handleChange={(value) => { setValue('birthdate', value!.toISOString().split('T')[0]) }}
              label={t("inputs.birthdate.label")}
              placeholder={t("inputs.birthdate.placeholder")}
              defaultValue={formValues.birthdate ? parseISO(formValues.birthdate) : undefined}
            />
          </div>
          <SelectSearchComponent
            className={`w-full ${isMobile ? 'mt-10' : ''}`}
            clearIndicator={false}
            options={genders}
            label={t('inputs.gender.label')}
            handleChange={(v) => { setValue('gender', v) }}
            value={formValues.gender}
            placeholder={t('inputs.gender.placeholder')}
            labelClassName="text-bodyLarge leading-none pb-3 group-disabled:cursor-not-allowed group-disabled:opacity-70"
          />
          <SelectSearchComponent
            className={`w-full ${isMobile ? 'mt-10' : ''}`}
            clearIndicator={false}
            options={countries[lang]}
            label={t('inputs.country.label')}
            handleChange={(v) => { setValue('countryCode', v) }}
            value={formValues.countryCode}
            placeholder={t('inputs.country.placeholder')}
            labelClassName="text-bodyLarge leading-none pb-3 group-disabled:cursor-not-allowed group-disabled:opacity-70"
          />
        </div>
        <div className="flex flex-row justify-end items-center pt-12 w-full">
          <Button
            label={t("buttons.submit")}
            size="mobile"
            type="submit"
            primary
            loading={loading}
          />
        </div>
      </form>
      <p className={'text-headingMedium'}>{t("settings.preferences")}</p>
      <div
        className={`flex flex-col justify-start items-center w-full mt-3 ${isMobile ? 'gap-y-3' : 'grid grid-cols-2 grid-rows-1 gap-3 '}`}
      >
        <SelectComponent
          className={`w-full ${isMobile ? 'mt-2' : ''}`}
          options={languages}
          label={t('inputs.language.label')}
          handleChange={(v) => { setLanguage(v as Lang) }}
          value={lang}
          placeholder={t('inputs.nationality.placeholder')}
          labelClassName="text-bodyLarge leading-none pb-3 group-disabled:cursor-not-allowed group-disabled:opacity-70"
        />
      </div>
      <div className={`flex w-full justify-center items-center pt-14 gap-x-2 gap-y-2 ${isMobile ? 'flex-col' : 'flex-row'}`}>
        <Button
          label={t('buttons.deleteAccount')}
          onClick={() => { setKeepDataModalOpen(true) }}
          size={isMobile ? 'large' : 'medium'}
        />
        <Button
          label={t('buttons.logout')}
          onClick={() => setLogoutModalOpen(true)}
          size={isMobile ? 'large' : 'medium'}
        />
      </div>
      <Modal
        title={t("settings.deleteModal.title")}
        subtitle={t("settings.deleteModal.subtitle")}
        open={deleteModalOpen}
        closeBtn
        primaryBtn={{
          label: t('buttons.confirm'),
          onClick: handleDeleteAccount,
        }}
        secondaryBtn={{
          label: t('buttons.cancel'),
          onClick: () => { setDeleteModalOpen(false) },
        }}
        onClose={() => setDeleteModalOpen(false)}
        header={<InfoIcon />}
      />
      <Modal
        title={t("settings.keepDataModal.title")}
        subtitle={t("settings.keepDataModal.subtitle")}
        open={keepDataModalOpen}
        closeBtn
        primaryBtn={{
          label: t('buttons.keepData'),
          onClick: () => {
            setDeleteModalOpen(true);
            setKeepDataModalOpen(false);
          },
        }}
        secondaryBtn={{
          label: t('buttons.eraseData'),
          onClick: () => {
            setEraseData(true);
            setDeleteModalOpen(true);
            setKeepDataModalOpen(false);
          },
        }}
        onClose={() => {
          setKeepDataModalOpen(false);
        }}
        header={<InfoIcon />}
      />
      <Modal
        title={t("settings.logoutModal.title")}
        subtitle={t("settings.logoutModal.subtitle")}
        open={logoutModalOpen}
        closeBtn
        primaryBtn={{
          label: t('buttons.confirm'),
          onClick: () => {
            setLogoutModalOpen(false);
            handleLogout();
          },
        }}
        secondaryBtn={{
          label: t('buttons.cancel'),
          onClick: () => {
            setLogoutModalOpen(false);
          },
        }}
        onClose={() => {
          setKeepDataModalOpen(false);
          setLogoutModalOpen(false);
        }}
        header={<InfoIcon />}
      />
    </div>
  )
}